import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { CheckCircle, MessageCircle, Phone, ArrowRight, Info } from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../components/ui/accordion';
import { ENQUIRIES_WEBHOOK_URL } from '../config/webhooks';

const WA_URL      = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20an%20admission%20enquiry';
const WA_FEES_URL = 'https://wa.me/919246887272?text=Hi%2C%20could%20you%20please%20share%20the%20fee%20details%3F';

const steps = [
  {
    num: 1,
    title: 'Say hello.',
    desc: 'WhatsApp, call, or send the enquiry form below. We\'ll find a convenient time for your visit.',
  },
  {
    num: 2,
    title: 'Tour the school.',
    desc: 'Walk through our environments with us. See the materials, feel the atmosphere, and ask absolutely anything. Most parents tell us this visit is when everything "clicks" — when they see how each material guides the child toward knowledge, independence and confidence.',
  },
  {
    num: 3,
    title: 'Your child\'s observation & parent interaction.',
    desc: 'If you\'d like to go ahead, we schedule a gentle observation session of your child with an AMI-trained guide, along with an interaction we\'d like to have with you to assess alignment. This isn\'t a test and there\'s nothing to prepare — every child learns differently, and observation helps us understand your child\'s individual developmental needs so we can receive them well.',
  },
  {
    num: 4,
    title: 'Admission.',
    desc: 'Collect the admission form, submit the documents, complete the fee — and your child\'s place in the House of Children is ready.',
  },
];

const faqs = [
  {
    value: 'ratio',
    title: 'What is your teacher–student ratio?',
    content: (
      <p className="text-slate-600 leading-relaxed">
        1:15 — and importantly, every environment has two AMI-trained guides. Montessori norms worldwide allow one trained adult (with an assistant) for 40–45 children, because the materials and the method carry so much of the teaching. We go well beyond that norm.
      </p>
    ),
  },
  {
    value: 'one-teacher',
    title: 'Only one teacher for so many children — how does that work?',
    content: (
      <p className="text-slate-600 leading-relaxed">
        It's one of Montessori's most misunderstood strengths. The environment and materials do roughly 98% of the teaching; children work independently once presented a material, older children help younger ones, and the guide gives precise one-to-one presentations. The result is <strong>more individual attention</strong> than a lecture-style class of 30 — not less. Come and watch it happen; it convinces faster than any explanation.
      </p>
    ),
  },
  {
    value: 'cbse-transition',
    title: 'Will my child cope if they later move to a CBSE/ICSE/State Board school?',
    content: (
      <p className="text-slate-600 leading-relaxed">
        Beautifully — this is the feedback we've heard for 25 years, without exception. Montessori children carry concentration, independence, strong language and fearless communication into any system. Our alumni have topped Class 10 in Hyderabad's leading schools; parents consistently tell us their children stand out in behaviour, attention and participation.
      </p>
    ),
  },
  {
    value: 'age',
    title: 'At what age should my child join?',
    content: (
      <p className="text-slate-600 leading-relaxed">
        The earlier in the 2–6 window, the better — the absorbent mind is at its peak, and the full three-year Primary cycle is where the magic compounds. But talk to us whatever your child's age; we'll advise honestly, even if that advice is "wait" or "elsewhere."
      </p>
    ),
  },
  {
    value: 'safety',
    title: 'Is the school safe and hygienic?',
    content: (
      <p className="text-slate-600 leading-relaxed">
        A safe, clean, prepared environment is a core Montessori principle, not an add-on. Children are inquisitive and touch everything — so everything is made safe and kept meticulously clean.
      </p>
    ),
  },
  {
    value: 'fees',
    title: 'What are the fees?',
    content: (
      <div className="space-y-3">
        <p className="text-slate-600 leading-relaxed">
          Fee details are shared on enquiry and during your school visit, so we can walk you through what's included for your child's specific program.
        </p>
        <a
          href={WA_FEES_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="faq-fees-whatsapp"
          className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-5 py-2.5 text-xs font-bold hover:bg-[#128C7E] transition-colors"
        >
          <MessageCircle size={14} /> Ask About Fees on WhatsApp
        </a>
      </div>
    ),
  },
];

const initialForm = {
  parentName: '',
  phone: '',
  email: '',
  childName: '',
  childDob: '',
  program: '',
  message: '',
  website: '', // honeypot — must stay empty
};

const inputClass = 'w-full rounded-2xl border-2 border-blue-100 bg-[#F4F7FB] px-5 py-3.5 text-slate-800 placeholder-slate-400 focus:border-[#60A5FA] focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all text-sm';
const labelClass = 'block text-sm font-semibold text-[#1E3A8A] mb-1.5';

export default function Admissions() {
  const [form, setForm]             = useState(initialForm);
  const [submitted, setSubmitted]   = useState(false);
  const [errors, setErrors]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.substring(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      }
    }
  }, [hash]);

  const validate = () => {
    const e = {};
    if (!form.parentName.trim()) e.parentName = 'Your name is required';
    if (!form.phone.trim()) e.phone = 'Mobile number is required';
    if (!form.childName.trim()) e.childName = "Child's name is required";
    if (!form.childDob.trim()) e.childDob = "Child's date of birth is required";
    return e;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Honeypot: bots fill this hidden field; humans leave it empty
    if (form.website) return;
    const v = validate();
    if (Object.keys(v).length > 0) { setErrors(v); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      if (ENQUIRIES_WEBHOOK_URL) {
        const payload = {
          source:      'Admissions',
          enquiryType: 'Enrolment',
          name:        form.parentName,
          mobile:      form.phone,
          email:       '',
          childDob:    form.childDob,
          program:     form.program,
          message:     form.message,
        };
        await fetch(ENQUIRIES_WEBHOOK_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
          mode:    'no-cors',
        });
      }
      setSubmitted(true);
    } catch (_) {
      setSubmitError('Something went wrong — please try again, or WhatsApp us at +91\u00a092468\u00a087272.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16">
      <Helmet>
        <title>Admissions | Sri Aurobindo Montessori House of Children, West Marredpally, Secunderabad</title>
        <meta name="description" content="Admissions for ages 2–12 at our AMI Montessori school in West Marredpally, Secunderabad. A simple, pressure-free process: visit, observe, decide. Enquire on WhatsApp or book your school tour today." />
      </Helmet>

      {/* ── BANNER ── */}
      <section className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Admissions</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="admissions-h1"
          >
            Choosing a school is a big decision.<br className="hidden sm:block" /> We make the process a small one.
          </h1>
        </div>
      </section>

      {/* ── OUR PROMISE FIRST ── */}
      <section className="py-16 bg-white" data-testid="promise-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Before anything else</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Our promise to you first
          </h2>
          <p className="text-slate-600 leading-relaxed text-base">
            We will never pressure you. In fact, we'll tell you what we tell every family: visit other Montessori schools too, research well, take your time. Our only request — for your child's overall development, don't compromise on an authentic environment.
          </p>
          <div className="mt-6 bg-[#EFF6FF] rounded-3xl px-7 py-5 border-l-4 border-[#1E3A8A]">
            <p className="text-[#1E3A8A] font-bold text-sm leading-relaxed">
              Families who choose us, choose us freely. That's how it has worked for 25 years, without a single advertisement.
            </p>
          </div>
        </div>
      </section>

      {/* ── STEPS ── */}
      <section className="py-16 bg-[#F4F7FB]" data-testid="steps-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">How It Works</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-10" style={{ fontFamily: 'Nunito, sans-serif' }}>
            The journey, step by step
          </h2>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-5 top-6 bottom-16 w-0.5 bg-[#DBEAFE]" aria-hidden="true" />

            <div className="space-y-8">
              {steps.map((step, i) => (
                <div key={i} data-testid={`step-${step.num}`} className="flex gap-6 items-start">
                  <div className="relative z-10 w-10 h-10 flex-shrink-0 rounded-full bg-[#1E3A8A] flex items-center justify-center shadow-md">
                    <span className="text-white font-black text-sm">{step.num}</span>
                  </div>
                  <div className="bg-white rounded-3xl px-7 py-6 flex-1 shadow-[0_2px_12px_rgba(59,130,246,0.06)]">
                    <h3 className="font-extrabold text-[#1E3A8A] text-base mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      Step {step.num} — {step.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Microcopy */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 text-center sm:text-left">
            <div className="bg-white rounded-2xl px-6 py-4 flex-1 shadow-sm border border-blue-50">
              <p className="text-xs text-slate-400 mb-1 uppercase tracking-wide font-bold">Typical timeline</p>
              <p className="text-[#1E3A8A] font-black text-sm">First enquiry → confirmed in 7 days</p>
            </div>
            <div className="bg-white rounded-2xl px-6 py-4 flex-1 shadow-sm border border-blue-50">
              <p className="text-xs text-slate-400 mb-1 uppercase tracking-wide font-bold">Admissions</p>
              <p className="text-[#1E3A8A] font-black text-sm">Year-round · child turning 2 yrs 6 months</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Common Questions</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-8" style={{ fontFamily: 'Nunito, sans-serif' }}>
            What parents usually ask us
          </h2>

          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.value}
                value={faq.value}
                data-testid={`faq-${faq.value}`}
                className="bg-[#F4F7FB] rounded-2xl border border-blue-50 px-6 overflow-hidden"
              >
                <AccordionTrigger className="text-[#1E3A8A] font-bold text-sm py-5 hover:no-underline hover:text-[#1E40AF] text-left">
                  {faq.title}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm">
                  {faq.content}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── START YOUR ENQUIRY ── */}
      <section className="py-14 bg-[#EFF6FF]" data-testid="enquiry-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Ready to talk?</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Start your enquiry
          </h2>
          <p className="text-slate-500 text-sm mb-8">Fastest reply on WhatsApp. Or call us during school hours.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enquiry-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2 shadow-md"
            >
              <MessageCircle size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>WhatsApp Us Now</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
            <a
              href="tel:9246887272"
              data-testid="enquiry-call-btn"
              className="border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <Phone size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>Call Us</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
          </div>
          <p className="mt-4 text-slate-400 text-xs">Mon – Fri, 9:00 am – 3:30 pm</p>
        </div>
      </section>

      {/* ── ENQUIRY FORM ── */}
      <section className="py-16 bg-white" id="enquiry-form" data-testid="form-section">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Enquiry Form</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Begin Your Child's Journey
            </h2>
            <p className="text-slate-400 text-sm">Share a few details and we'll reach out within two working days. No obligation, no pressure — that's a 25 year promise.</p>
          </div>

          {submitted ? (
            <div className="bg-[#EFF6FF] rounded-3xl p-10 text-center shadow-[0_4px_24px_rgba(59,130,246,0.08)]" data-testid="form-success">
              <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
                <CheckCircle size={28} className="text-[#1E3A8A]" />
              </div>
              <h3 className="text-xl font-extrabold text-[#1E3A8A] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>Thank you!</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                We'll reach out within 1 working day. If you'd like a faster reply, WhatsApp us at{' '}
                <strong>+91 92468 87272</strong>.
              </p>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="success-whatsapp-link"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-3 text-sm font-bold hover:bg-[#128C7E] transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp for faster response
              </a>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-8 shadow-[0_4px_30px_rgba(59,130,246,0.08)] border border-blue-50"
              data-testid="enquiry-form"
              noValidate
            >
              {/* Honeypot — bots fill this; humans don't */}
              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                <label htmlFor="adm-website">Leave this empty</label>
                <input
                  type="text"
                  id="adm-website"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-5">

                {/* Parent name */}
                <div>
                  <label className={labelClass}>Your name <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    name="parentName"
                    value={form.parentName}
                    onChange={handleChange}
                    placeholder="Your name"
                    data-testid="input-parent-name"
                    className={inputClass}
                  />
                  {errors.parentName && <p className="text-red-500 text-xs mt-1">{errors.parentName}</p>}
                </div>

                {/* Mobile */}
                <div>
                  <label className={labelClass}>Mobile number <span className="text-red-400">*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="WhatsApp-preferred number"
                    data-testid="input-phone"
                    className={inputClass}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className={labelClass}>
                    Email <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="e.g. priya@gmail.com"
                    data-testid="input-email"
                    className={inputClass}
                  />
                </div>

                {/* Child name */}
                <div>
                  <label className={labelClass}>Child's name <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    name="childName"
                    value={form.childName}
                    onChange={handleChange}
                    placeholder="e.g. Arjun Sharma"
                    data-testid="input-child-name"
                    className={inputClass}
                  />
                  {errors.childName && <p className="text-red-500 text-xs mt-1">{errors.childName}</p>}
                </div>

                {/* Child DOB */}
                <div>
                  <label className={labelClass}>Child's date of birth <span className="text-red-400">*</span></label>
                  <input
                    type="date"
                    name="childDob"
                    value={form.childDob}
                    onChange={handleChange}
                    data-testid="input-child-dob"
                    className={inputClass}
                  />
                  <p className="text-slate-400 text-xs mt-1">This helps us suggest the right program.</p>
                  {errors.childDob && <p className="text-red-500 text-xs mt-1">{errors.childDob}</p>}
                </div>

                {/* Program */}
                <div>
                  <label className={labelClass}>Program interested in</label>
                  <select
                    name="program"
                    value={form.program}
                    onChange={handleChange}
                    data-testid="input-program"
                    className={inputClass}
                  >
                    <option value="">Select a program</option>
                    <option value="toddler">Toddler (2–2.5 yrs)</option>
                    <option value="primary">Primary (2.5–6 yrs)</option>
                    <option value="elementary">Elementary (6–12 yrs)</option>
                    <option value="unsure">Not sure — please guide me</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className={labelClass}>
                    Message <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Anything you'd like us to know?"
                    data-testid="input-message"
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>

              {/* Network error banner */}
              {submitError && (
                <div className="mt-5 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3" data-testid="admissions-submit-error">
                  <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-800 text-sm leading-relaxed">{submitError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="enquiry-submit-btn"
                className="mt-6 w-full bg-[#1E3A8A] text-white rounded-full py-4 font-black text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : (<>Request a Call Back <ArrowRight size={16} /></>)}
              </button>
              <p className="text-center text-slate-400 text-xs mt-4">
                Prefer WhatsApp?{' '}
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-bold hover:underline">
                  Message us directly
                </a>
              </p>
            </form>
          )}
        </div>
      </section>

    </div>
  );
}
